import { z } from 'zod';
// === Producers ===
export const ProducerSummary = z.object({
    id: z.string(),
    name: z.string(),
});
export const CreateProducerRequest = z.object({
    name: z.string().min(1).max(200),
});
// === Product groups ===
export const ProductGroupSummary = z.object({
    id: z.string(),
    code: z.string(),
    label: z.string(),
    status: z.enum(['active', 'archived_in_terms']),
});
export const CreateProductGroupRequest = z.object({
    label: z.string().min(1).max(200),
    code: z.string().regex(/^[A-Z0-9_]+$/).max(60).optional(),
});
// === Products ===
const Gtin = z.string().regex(/^\d{14}$/).nullable();
export const ProductSummary = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    unit: z.string(),
    producerId: z.string(),
    producerName: z.string(),
    productGroupId: z.string(),
    productGroupLabel: z.string(),
    temperatureMode: z.string(),
    category: z.string(),
    archived: z.boolean(),
    requiresMarking: z.boolean(),
    gtinUnit: z.string().nullable(),
    gtinBlock: z.string().nullable(),
    gtinBox: z.string().nullable(),
    unitsPerBlock: z.number().int(),
    blocksPerBox: z.number().int(),
    updatedAt: z.string(),
    /**
     * Цена единицы товара для юрлица КЛИЕНТА по действующим КУ.
     *
     * Поле опционально и появляется только в ответе клиенту (role=client): юрлицо
     * сервер берёт из client_profiles сам, подменить его параметром нельзя.
     * `null` — на группу товара нет действующей цены (нет КУ / архив / нет строки).
     * Для сотрудников поле отсутствует — контракт для них не менялся.
     */
    clientUnitPrice: z.string().nullable().optional(),
});
export const CreateProductRequest = z.object({
    code: z.string().min(1).max(60),
    name: z.string().min(1).max(500),
    unit: z.string().default('шт.'),
    producerId: z.string().uuid(),
    productGroupId: z.string().uuid(),
    temperatureMode: z.string().default('+15 +25'),
    category: z.string().default('Кондиция'),
    requiresMarking: z.boolean().default(true),
    gtinUnit: Gtin.optional(),
    gtinBlock: Gtin.optional(),
    gtinBox: Gtin.optional(),
    unitsPerBlock: z.number().int().min(1).default(10),
    blocksPerBox: z.number().int().min(1).default(50),
});
export const PatchProductRequest = z.object({
    code: z.string().min(1).max(60).optional(),
    name: z.string().min(1).max(500).optional(),
    unit: z.string().optional(),
    producerId: z.string().uuid().optional(),
    productGroupId: z.string().uuid().optional(),
    temperatureMode: z.string().optional(),
    category: z.string().optional(),
    archived: z.boolean().optional(),
    requiresMarking: z.boolean().optional(),
    gtinUnit: Gtin.optional(),
    gtinBlock: Gtin.optional(),
    gtinBox: Gtin.optional(),
    unitsPerBlock: z.number().int().min(1).optional(),
    blocksPerBox: z.number().int().min(1).optional(),
});
// === Legal entities ===
export const LegalEntityStatus = z.enum(['active', 'archived_in_terms', 'manual']);
export const LegalEntitySummary = z.object({
    id: z.string(),
    name: z.string(),
    inn: z.string().nullable(),
    status: LegalEntityStatus,
    source: z.enum(['manual', 'price_import']),
    warehouse: z.string().nullable(),
    city: z.string().nullable(),
    brandLabel: z.string().nullable(),
    phone: z.string().nullable(),
    contactName: z.string().nullable(),
    contactRole: z.string().nullable(),
    email: z.string().nullable(),
    telegram: z.string().nullable(),
    edoId: z.string().nullable(),
    paymentDelay: z.number().int().nullable(),
    deliveryTime: z.number().int().nullable(),
    creditLimit: z.string().nullable(),
    pdzDelay: z.number().int().nullable(),
    entityKind: z.enum(['company', 'entrepreneur']).nullable(),
    ogrn: z.string().nullable(),
    kpp: z.string().nullable(),
    legalAddress: z.string().nullable(),
    directorName: z.string().nullable(),
    directorShort: z.string().nullable(),
    directorTitle: z.string().nullable(),
    signatoryBasis: z.string().nullable(),
    bankName: z.string().nullable(),
    bankAccount: z.string().nullable(),
    bankCorr: z.string().nullable(),
    bankBik: z.string().nullable(),
    updatedAt: z.string(),
    recentlyUsed: z.boolean().optional(),
});
export const CreateLegalEntityRequest = z.object({
    name: z.string().min(1).max(500),
});
export const PatchLegalEntityRequest = z.object({
    name: z.string().min(1).max(500).optional(),
    warehouse: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    brandLabel: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    contactName: z.string().nullable().optional(),
    contactRole: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    telegram: z.string().nullable().optional(),
    edoId: z.string().nullable().optional(),
    paymentDelay: z.number().int().nullable().optional(),
    deliveryTime: z.number().int().nullable().optional(),
    creditLimit: z.string().nullable().optional(),
    pdzDelay: z.number().int().nullable().optional(),
    entityKind: z.enum(['company', 'entrepreneur']).nullable().optional(),
    ogrn: z.string().nullable().optional(),
    kpp: z.string().nullable().optional(),
    legalAddress: z.string().nullable().optional(),
    directorName: z.string().nullable().optional(),
    directorShort: z.string().nullable().optional(),
    directorTitle: z.string().nullable().optional(),
    signatoryBasis: z.string().nullable().optional(),
    bankName: z.string().nullable().optional(),
    bankAccount: z.string().nullable().optional(),
    bankCorr: z.string().nullable().optional(),
    bankBik: z.string().nullable().optional(),
});
// === Simple catalogs ===
export const SimpleCatalogKind = z.enum(['carrier', 'vehicle', 'driver', 'extra_service', 'order_type', 'warehouse']);
export const SimpleCatalogItem = z.object({
    id: z.string(),
    kind: SimpleCatalogKind,
    value: z.string(),
    isDefault: z.boolean(),
    archived: z.boolean(),
    displayLabel: z.string().nullable(),
    isTransfer: z.boolean(),
    requiresShipmentDetails: z.boolean(),
});
export const CreateSimpleCatalogRequest = z.object({
    value: z.string().min(1).max(200),
    isDefault: z.boolean().optional(),
});
export const PatchSimpleCatalogRequest = z.object({
    value: z.string().min(1).max(200).optional(),
    isDefault: z.boolean().optional(),
    archived: z.boolean().optional(),
    requiresShipmentDetails: z.boolean().optional(),
});
//# sourceMappingURL=catalogs.js.map